import type { MessageDescriptor } from "@bundle/core";
import { Avatar, Banner, Button, Icon, Stack, Text } from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";

/** Floating "3 new messages ↑" pill over the timeline. */
export function NewMessagesPill({ count, onPress }: { count: number; onPress: () => void }) {
  const t = useT();
  return <Button label={t("channel.newMessagesJump", { count })} icon="arrowUp" variant="glass" size="sm" onPress={onPress} testID="new-messages-pill" />;
}

/** Top of a conversation's history: "This is the very beginning of #design". */
export function ChannelIntro({
  title,
  isDm,
  isPrivate,
  avatar,
  prompt,
}: {
  title: string;
  isDm: boolean;
  isPrivate?: boolean;
  avatar?: { name: string; src?: string; square?: boolean };
  /** The line under the title (ChannelViewModel `introPrompt`, translated); absent = none (archived channels). */
  prompt?: string;
}) {
  const t = useT();
  return (
    <Stack gap="2" paddingX="5" paddingY="6" testID="channel-intro">
      {avatar === undefined ? (
        <Stack direction="horizontal" align="center">
          <Stack padding="3" radius="lg" background="accent.subtle">
            <Icon name={isDm ? "users" : isPrivate === true ? "lock" : "hash"} size="icon.lg" color="accent.text" />
          </Stack>
        </Stack>
      ) : (
        <Avatar name={avatar.name} src={avatar.src} size="avatar.xl" square={avatar.square} />
      )}
      <Text variant="title3" weight="bold">
        {isDm ? title : t("channel.empty.title", { channel: title })}
      </Text>
      {prompt === undefined ? null : <Text color="text.secondary">{prompt}</Text>}
    </Stack>
  );
}

/** Subtle banner shown in channels that include people from other organizations. */
export function ExternalBanner({ label }: { label: MessageDescriptor }) {
  const tr = useTr();
  return <Banner tone="external" placement="edge" message={tr(label)} testID="external-banner" />;
}

/** Centered placeholder for features that arrive in a later milestone. */
export function ComingSoon({ icon, title, body }: { icon: Parameters<typeof Icon>[0]["name"]; title: string; body: string }) {
  return (
    <Stack grow align="center" justify="center" gap="3" padding="8" testID="coming-soon">
      <Stack padding="4" radius="xl" background="bg.surface" border="border.subtle">
        <Icon name={icon} size="icon.lg" color="text.secondary" />
      </Stack>
      <Text variant="title3" weight="semibold" align="center">
        {title}
      </Text>
      <Text color="text.secondary" align="center">
        {body}
      </Text>
    </Stack>
  );
}
